import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { cn } from '@/shared/ui/lib/utils';

type EmblaApi = ReturnType<typeof useEmblaCarousel>[1];

// 캐러셀을 바깥(부모)에서 제어하기 위한 공개 타입(shadcn setApi 패턴).
// 부모는 이 api 로 selectedScrollSnap()/scrollSnapList()/scrollNext() 등을 사용한다.
export type CarouselApi = EmblaApi;

// useSyncExternalStore에서 빈 배열 반환 시 무한 루프 방지용 상수
const EMPTY_ARRAY: number[] = [];

type CarouselContextType = {
  emblaApi: EmblaApi;
  emblaRef: ReturnType<typeof useEmblaCarousel>[0];
  scrollSnaps: number[];
  selectedIndex: number;
};

// 하위 컴포넌트에서 Embla 상태를 사용하기 위한 Context
const CarouselContext = createContext<CarouselContextType | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel must be used within a CarouselRoot');
  }
  return context;
}

// 현재 선택된 슬라이드 인덱스를 React 상태와 동기화
function useEmblaSelectedIndex(emblaApi: EmblaApi) {
  // Embla의 select, reInit 이벤트 구독
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!emblaApi) return () => {};
      emblaApi.on('select', callback);
      emblaApi.on('reInit', callback);
      return () => {
        emblaApi.off('select', callback);
        emblaApi.off('reInit', callback);
      };
    },
    [emblaApi]
  );

  // 현재 선택된 인덱스 반환
  const getSnapshot = useCallback(() => {
    return emblaApi?.selectedScrollSnap() ?? 0;
  }, [emblaApi]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

// 슬라이드 개수(스냅 포인트)를 React 상태와 동기화
function useEmblaScrollSnaps(emblaApi: EmblaApi) {
  // reInit 이벤트만 구독 (슬라이드 개수는 자주 변경되지 않음)
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!emblaApi) return () => {};
      emblaApi.on('reInit', callback);
      return () => {
        emblaApi.off('reInit', callback);
      };
    },
    [emblaApi]
  );

  // 스냅 포인트 배열 반환 (EMPTY_ARRAY로 참조 안정성 보장)
  const getSnapshot = useCallback(() => {
    return emblaApi?.scrollSnapList() ?? EMPTY_ARRAY;
  }, [emblaApi]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

type CarouselRootProps = {
  autoPlay?: boolean;
  autoPlayDelay?: number;
  children: React.ReactNode;
  // 마지막 슬라이드에서 다시 처음으로 순환할지 여부(기본 true). 보고서처럼 "끝"이
  // 명확해야 하는 곳은 false 로 둔다.
  loop?: boolean;
  // 부모가 캐러셀 api 를 넘겨받기 위한 콜백(shadcn setApi 패턴).
  setApi?: (api: CarouselApi) => void;
};

// Embla 초기화 및 Context Provider
function CarouselRoot({
  children,
  loop = true,
  autoPlay = false,
  autoPlayDelay = 3000,
  setApi,
}: CarouselRootProps) {
  const plugins = useMemo(
    () =>
      autoPlay
        ? [Autoplay({ delay: autoPlayDelay, stopOnInteraction: false })]
        : [],
    [autoPlay, autoPlayDelay]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop }, plugins);
  const selectedIndex = useEmblaSelectedIndex(emblaApi);
  const scrollSnaps = useEmblaScrollSnaps(emblaApi);

  // api 가 준비되면 부모에게 전달한다. setApi 가 stable(예: useState setter)이면
  // 매 렌더마다 재호출돼도 동일 값이라 부모 상태는 바뀌지 않는다.
  useEffect(() => {
    if (emblaApi && setApi) setApi(emblaApi);
  }, [emblaApi, setApi]);

  return (
    <CarouselContext.Provider
      value={{ emblaApi, emblaRef, scrollSnaps, selectedIndex }}
    >
      <div aria-roledescription="carousel" role="region">
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

// 슬라이드 뷰포트 (emblaRef 연결)
function CarouselContent({ children }: { children: React.ReactNode }) {
  const { emblaRef } = useCarousel();

  return (
    <div ref={emblaRef} className="overflow-hidden">
      <div className="flex">{children}</div>
    </div>
  );
}

// 개별 슬라이드 아이템
function CarouselItem({ children }: { children: React.ReactNode }) {
  return (
    <div
      aria-roledescription="slide"
      className="min-w-0 shrink-0 grow-0 basis-full"
      role="group"
    >
      {children}
    </div>
  );
}

// 하단 dot 인디케이터
function CarouselPagination({ className }: { className?: string }) {
  const { emblaApi, scrollSnaps, selectedIndex } = useCarousel();

  return (
    <div className={cn('flex justify-center gap-2', className)}>
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          aria-label={`${index + 1}번째 슬라이드로 이동`}
          type="button"
          className={cn(
            'h-2 w-2 rounded-full transition-all duration-300 ease-out',
            index === selectedIndex ? 'w-6 bg-white' : 'bg-default-600'
          )}
          onClick={() => emblaApi?.scrollTo(index)}
        />
      ))}
    </div>
  );
}

export const Carousel = {
  Root: CarouselRoot,
  Content: CarouselContent,
  Item: CarouselItem,
  Pagination: CarouselPagination,
};
